import type { ReactElement } from "react";
import { Navigate } from "@tanstack/react-router";
import { useBandContextOrNull, useCapability } from "../../app/bandContext.js";

/**
 * The CREW FALL-THROUGH for the Assets page — what a seat WITHOUT `assets:write` gets where that page
 * would be. `null` means "nothing to fall through to": the caller renders its own role-denied empty.
 *
 * The rule is unchanged (CD: "production crew see no press kit at all — an old /assets bookmark falls
 * through to the production stack rather than an empty page"), but it is a BAND-PATH rule and is now
 * scoped to say so. It is about the production trio, whose `assets=venue_only` grants no `assets:write`
 * and whose real asset door is Advance Docs. Under the ROSTER lens none of that holds: there is no
 * `/advance` route (every roster module lives at `/roster/artists/{artistId}/{tab}`), there is no crew
 * seat, and since 2026-09-01 a roster lens EDITS any page it can see — so a roster seat has no business
 * in this branch at all.
 *
 * TWO ways that went wrong, both of which this shape makes impossible:
 * - Navigating to `/advance` from under the org lens leaves the roster shell ENTIRELY and lands an ORG
 *   seat in the artist shell, which has no band for it: `RequireBand`'s "No band selected". That is how
 *   the Booking lens's Assets tab failed from the day it shipped.
 * - Rendering the production pack IN PLACE instead fixed the lens crossing and kept a second problem:
 *   the page header still reads "Assets · Booking" over a screen titled "Advance Docs", which is a
 *   surface lying about which page you are on.
 *
 * So the roster branch is `null` — the caller's role-denied empty, which at least names what happened.
 * In practice no roster lens reaches it now (all three hold `assets:write`); the guard is what stops a
 * mis-seeded or half-deployed one from resurrecting either failure.
 */
export function useAssetsCrewFallthrough(): ReactElement | null {
  const canProduction = useCapability("production:read");
  const shell = useBandContextOrNull()?.shell;
  if (!canProduction || shell === "roster") return null;
  return <Navigate to="/advance" replace />;
}
